import { useCombatStore } from '@/state/useCombatStore'
import { WEAPONS, WEAPON_ORDER } from '@/combat/weaponTypes'
import { useShipStore } from '@/state/useShipStore'
import './CombatHUD.css'

const ABILITY_DISPLAY: Record<string, string> = {
  dodge_roll: 'Dodge Roll',
  block: 'Block',
  power_strike: 'Power Strike',
  quick_reload: 'Quick Reload',
  steady_aim: 'Steady Aim',
}

export function CombatHUD() {
  const aboard = useShipStore((s) => s.aboard)
  const health = useCombatStore((s) => s.health)
  const maxHealth = useCombatStore((s) => s.maxHealth)
  const equippedWeapon = useCombatStore((s) => s.equippedWeapon)
  const unlockedWeapons = useCombatStore((s) => s.unlockedWeapons)
  const abilityCooldown = useCombatStore((s) => s.abilityCooldown)
  const isDodging = useCombatStore((s) => s.isDodging)
  const isReloading = useCombatStore((s) => s.isReloading)
  const reloadTimer = useCombatStore((s) => s.reloadTimer)
  const isSteadyAiming = useCombatStore((s) => s.isSteadyAiming)

  // Hide when aboard a ship
  if (aboard) return null

  const info = WEAPONS[equippedWeapon]
  const healthPct = (health / maxHealth) * 100
  const abilityReady = abilityCooldown <= 0
  const reloadTotal = info.reloadTime ?? 1
  const reloadPct = isReloading ? ((reloadTotal - reloadTimer) / reloadTotal) * 100 : 100
  const isRanged = info.category === 'ranged' || info.category === 'thrown'

  return (
    <>
      <div className="combat-hud">
        {/* Health */}
        <div className="combat-hud__health">
          <div className="combat-hud__health-label">Health</div>
          <div className="combat-hud__health-bar">
            <div
              className="combat-hud__health-fill"
              style={{
                width: `${healthPct}%`,
                backgroundColor: healthPct > 60 ? '#4a8' : healthPct > 30 ? '#a84' : '#a44',
              }}
            />
          </div>
          <div className="combat-hud__health-text">{health} / {maxHealth}</div>
        </div>

        {/* Equipped weapon */}
        <div className="combat-hud__weapon">
          <div className="combat-hud__weapon-name">{info.name}</div>
          <div className="combat-hud__weapon-stats">
            <div className="combat-hud__weapon-stat">
              DMG <span className="combat-hud__weapon-stat-val">{info.damage}</span>
            </div>
            <div className="combat-hud__weapon-stat">
              SPD <span className="combat-hud__weapon-stat-val">{info.attackSpeed.toFixed(1)}</span>
            </div>
            <div className="combat-hud__weapon-stat">
              RNG <span className="combat-hud__weapon-stat-val">{info.range}</span>
            </div>
          </div>
        </div>

        {/* Weapon slots */}
        <div className="combat-hud__slots">
          {WEAPON_ORDER.map((w, i) => {
            const wInfo = WEAPONS[w]
            const unlocked = unlockedWeapons.includes(w)
            const active = equippedWeapon === w
            const cls = [
              'combat-hud__slot',
              active ? 'combat-hud__slot--active' : '',
              !unlocked ? 'combat-hud__slot--locked' : '',
            ].join(' ')
            return (
              <div key={w} className={cls}>
                <span className="combat-hud__slot-key">{i + 1}</span>
                <span className="combat-hud__slot-name">{unlocked ? wInfo.name : '???'}</span>
              </div>
            )
          })}
        </div>

        {/* Ability */}
        <div className="combat-hud__ability">
          <span className={`combat-hud__ability-key ${abilityReady ? 'combat-hud__ability-key--ready' : 'combat-hud__ability-key--cooldown'}`}>
            R
          </span>
          <div className="combat-hud__ability-info">
            <span className="combat-hud__ability-name">{ABILITY_DISPLAY[info.ability]}</span>
            {!abilityReady && (
              <span className="combat-hud__ability-cd">{abilityCooldown.toFixed(1)}s</span>
            )}
          </div>
        </div>

        {/* Reload bar (ranged only) */}
        {isRanged && isReloading && (
          <div className="combat-hud__reload">
            <div className="combat-hud__reload-label">Reloading...</div>
            <div className="combat-hud__reload-bar">
              <div className="combat-hud__reload-fill" style={{ width: `${reloadPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Dodge flash overlay */}
      {isDodging && <div className="combat-hud__dodge-overlay" />}

      {/* Crosshair for ranged weapons */}
      {isRanged && (
        <div className={`combat-hud__crosshair ${isSteadyAiming ? 'combat-hud__crosshair--steady' : ''}`}>
          <div className="combat-hud__crosshair-dot" />
        </div>
      )}
    </>
  )
}
